import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, AutoComplete,
  InputNumber, Select, Col, message, Divider, Tag, Tooltip, Popconfirm,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined,
  UserOutlined, EyeOutlined, ThunderboltOutlined,
  CheckCircleOutlined, EnvironmentOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type Consumer, type ConsumerService } from '@/context/MasterDataContext'
import { useLeadCompanies } from '@/pages/Lead/useLeadCompanies'
import { leadStore } from '@/pages/Lead/leadStore'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import { useModulePermission } from '@/hooks/useModulePermission'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import { formatIndianCompact, annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'
import { ApiError } from '@/services/api'
import './MasterForm.css'

const BASE = '/master/customers'

const STATUS_COLOR: Record<string, string> = {
  active: '#059669', suspended: '#DC2626', inactive: '#8A93A6',
}


const STATE_OPTIONS   = [
  'Andhra Pradesh', 'Gujarat', 'Karnataka', 'Maharashtra',
  'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh',
]

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 160, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const nextServiceId = (services: ConsumerService[]): string => {
  const nums = services.map(s => parseInt(s.id.replace(/\D/g, '')) || 0)
  return `SRV${String(Math.max(0, ...nums) + 1).padStart(3, '0')}`
}

const CustomerMaster: React.FC = () => {
  const { consumers, powerTypes, supplyModes, tnebTariffs, edcs, drawalVoltages, deleteConsumer, saveConsumer, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goView, goList } = useMasterFormRoute(BASE)
  const { canAdd, canEdit, canDelete } = useModulePermission('Consumer')
  // Record shown by the full-page Detail view (mode === 'view').
  const viewRecord: Consumer | undefined = mode === 'view' ? consumers.find(c => c.id === editId) : undefined
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  /* Services sub-state */
  const [services, setServices]               = useState<ConsumerService[]>([])
  const [serviceModalOpen, setServiceModalOpen] = useState(false)
  const [editingServiceIdx, setEditingServiceIdx] = useState<number | null>(null)
  const [serviceForm] = Form.useForm()
  const serviceConsumptionWatch = Form.useWatch('avgAnnualConsumption', serviceForm)

  /* List search / filter */
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const tnebTariffOptions = tnebTariffs.filter(t => t.status === 'active').map(t => ({ value: t.code, label: `${t.code} — ${t.description}` }))
  const edcOptions        = edcs.filter(e => e.status === 'active').map(e => ({ value: e.name, label: `${e.name} (${e.place})` }))
  const voltageOptions    = drawalVoltages.filter(v => v.status === 'active').map(v => ({ value: v.value, label: `${v.value} ${v.unit}` }))
  const typeOfPowerOptions    = powerTypes.filter(p => p.status === 'active').map(p => ({ value: p.name, label: p.name }))
  const modeOfSupplyOptions   = supplyModes.filter(m => m.status === 'active').map(m => ({ value: m.name, label: m.name }))

  // Company names from Consumer-type leads, suggested in the name field
  // below — useLeadCompanies itself now gates this on Rule §14 (approved
  // proposal + signed agreement), so a lead isn't selectable here the moment
  // it's created, only once that deal is actually closed.
  const leadCompanies = useLeadCompanies('Consumer')

  const activeCount = consumers.filter(c => c.status === 'active').length
  const totalConsumption = consumers.reduce((s, c) => {
    const serviceSum = (c.services || []).reduce((ss, srv) => ss + Number(srv.avgAnnualConsumption || 0), 0)
    return s + serviceSum
  }, 0)
  const statesCount = new Set(consumers.map(c => c.state)).size

  const filteredConsumers = consumers.filter(c => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      c.name.toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q) ||
      (c.gstin || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || c.status === filterStatus
    return matchSearch && matchStatus
  })

  /* ── Populate the form when entering the full-page add/edit route ── */
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      setServices([])
    } else if (mode === 'edit') {
      const c = consumers.find(r => r.id === editId)
      if (c) {
        form.setFieldsValue({
          name: c.name, contactPersonName: c.contactPersonName, designation: c.designation,
          contactMobile: c.contactMobile, contactEmail: c.contactEmail,
          companyAddress: c.companyAddress, gstin: c.gstin, panNumber: c.panNumber, cinNumber: c.cinNumber,
          state: c.state, tariffCode: c.tariffCode, tdsRate: c.tdsRate,
          paymentTerms: c.paymentTerms, bankDetails: c.bankDetails, status: c.status,
        })
        setServices(c.services || [])
      } else {
        goList()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, consumers])

  const handleDelete = async (id: string) => {
    try {
      await deleteConsumer(id)
      message.success('Consumer removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  /* ── Pick a Consumer-type lead → auto-fill company contact details ── */
  const handleLeadCompanySelect = (companyName: string) => {
    const lead = leadStore.getLeads().find(
      l => (l.companyName || '').trim().toLowerCase() === companyName.trim().toLowerCase()
    )
    if (!lead) return
    const address = [lead.address, lead.city, lead.pinCode].map(s => (s || '').trim()).filter(Boolean).join(', ')
    form.setFieldsValue({
      name: lead.companyName,
      contactPersonName: lead.customerName || undefined,
      contactMobile: lead.contactNumber || undefined,
      contactEmail: lead.email || undefined,
      companyAddress: address || undefined,
      gstin: lead.gstNumber || undefined,
    })

    message.success(`Details filled from lead "${lead.companyName}" (Sanctioned Demand: ${lead.sanctionedDemand || 0} ${lead.demandUnit || 'KW'}, Drawal Voltage: ${lead.drawlVoltage || '—'}). Use "Add Service" to add HT service details.`)
  }

  /* ── Save consumer ── */
  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    const nameTrim = (values.name || '').trim().toLowerCase()
    const gstinTrim = (values.gstin || '').trim().toLowerCase()
    const firstSvcHtsc = (services[0]?.htscNumber || '').trim().toLowerCase()

    // ── Duplication Check: Company Name, GSTIN, or HTSC Number ──
    const duplicate = consumers.find(c => {
      if (isEdit && c.id === editId) return false
      const sameName = (c.name || '').trim().toLowerCase() === nameTrim
      const sameGst = gstinTrim && (c.gstin || '').trim().toLowerCase() === gstinTrim
      const sameHtsc = firstSvcHtsc && c.services?.some(s => (s.htscNumber || '').trim().toLowerCase() === firstSvcHtsc)
      return sameName || sameGst || sameHtsc
    })

    if (duplicate) {
      message.error(`Duplication not allowed: A consumer with company name "${duplicate.name}" ${duplicate.gstin ? `or GST "${duplicate.gstin}"` : ''} already exists in Consumer Master.`)
      return
    }

    let newCode: string
    if (isEdit) {
      newCode = editId!
    } else {
      try {
        newCode = await generateCode('consumers')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a code — check your connection and try again.")
        return
      }
    }

    const firstSvc = services[0]
    const allPower = [...new Set(services.flatMap(s => s.typeOfPower || []))]
    const allMode  = [...new Set(services.flatMap(s => s.modeOfSupply || []))]
    const totalSanctioned = services.reduce((sum, s) => sum + Number(s.sanctionedDemand || 0), 0)

    const payload: Consumer = {
      ...values,
      services,
      typeOfPower: allPower,
      modeOfSupply: allMode,
      htscNumber: firstSvc?.htscNumber || '',
      sanctionedDemand: totalSanctioned,
      powerTariffCategory: firstSvc?.powerTariffCategory || '',
      tariffCode: values.tariffCode || firstSvc?.powerTariffCategory || '',
      status: values.status || 'active',
      ...(isEdit ? { id: editId! } : { id: newCode, code: newCode }),
    }

    setSubmitting(true)
    try {
      await saveConsumer(payload, isEdit)
      message.success(isEdit ? 'Consumer updated' : 'Consumer added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  /* ── Service sub-modal ── */
  const openAddService = () => {
    setEditingServiceIdx(null)
    serviceForm.resetFields()
    const compName = form.getFieldValue('name')
    const matchedLead = compName
      ? leadStore.getLeads().find(l => (l.companyName || '').trim().toLowerCase() === (compName || '').trim().toLowerCase())
      : undefined

    serviceForm.setFieldsValue({
      status: 'active',
      sanctionedDemand: matchedLead?.sanctionedDemand ? Number(matchedLead.sanctionedDemand) : undefined,
      drawlVoltage: matchedLead?.drawlVoltage || undefined,
      htscNumber: matchedLead?.htscNumber || undefined,
      edc: matchedLead?.edcName || undefined,
      typeOfPower: matchedLead?.typeOfPower ? [matchedLead.typeOfPower] : undefined,
      modeOfSupply: matchedLead?.modeOfSupply ? [matchedLead.modeOfSupply] : undefined,
      avgAnnualConsumption: matchedLead?.requiredUnitsPerAnnum ? Number(matchedLead.requiredUnitsPerAnnum) : undefined,
    })
    setServiceModalOpen(true)
  }

  const openEditService = (idx: number) => {
    setEditingServiceIdx(idx)
    serviceForm.setFieldsValue(services[idx])
    setServiceModalOpen(true)
  }

  const handleSaveService = () => {
    serviceForm.validateFields().then(vals => {
      const svc: ConsumerService = {
        id: editingServiceIdx !== null ? services[editingServiceIdx].id : nextServiceId(services),
        plantName: vals.plantName || '',
        htscNumber: vals.htscNumber || '',
        plantAddress: vals.plantAddress || '',
        typeOfPower: vals.typeOfPower || [],
        typeOfPowerOther: vals.typeOfPowerOther || '',
        modeOfSupply: vals.modeOfSupply || [],
        modeOfSupplyOther: vals.modeOfSupplyOther || '',
        sanctionedDemand: vals.sanctionedDemand || 0,
        drawlVoltage: vals.drawlVoltage || '',
        substationName: vals.substationName || '',
        substationVoltage: vals.substationVoltage || '',
        edc: vals.edc || '',
        powerTariffCategory: vals.powerTariffCategory || '',
        existingPowerSupplier: vals.existingPowerSupplier || '',
        avgAnnualConsumption: vals.avgAnnualConsumption || 0,
        avgMonthlyBill: vals.avgMonthlyBill || 0,
        plantLatitude: vals.plantLatitude || '',
        plantLongitude: vals.plantLongitude || '',
        status: vals.status || 'active',
      }
      if (editingServiceIdx !== null) {
        setServices(prev => prev.map((s, i) => i === editingServiceIdx ? svc : s))
      } else {
        setServices(prev => [...prev, svc])
      }
      setServiceModalOpen(false)
      serviceForm.resetFields()
    }).catch(() => {
      message.error('Please fill in all required service fields.')
    })
  }

  const handleDeleteService = (idx: number) => {
    setServices(prev => prev.filter((_, i) => i !== idx))
  }

  /* ── Table columns ── */
  const columns = [
    {
      title: 'Consumer Name', dataIndex: 'name',
      render: (name: string, r: Consumer) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1677ff' }}>
            {name}
          </div>
          <div style={{ fontSize: 11, color: '#8A93A6', ...MONO }}>
            {r.code} · {(r.services || []).length} service{(r.services || []).length !== 1 ? 's' : ''}
          </div>
          {r.contactPersonName && (
            <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.contactPersonName} · {r.designation}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Type of Power', dataIndex: 'typeOfPower',
      render: (v: string[]) => (
        <>{(v || []).map(t => <Tag key={t} style={{ borderRadius: 6, fontWeight: 600, marginBottom: 2 }}>{t}</Tag>)}</>
      ),
    },
    {
      title: 'Sanctioned Demand', dataIndex: 'sanctionedDemand', align: 'right' as const,
      render: (v: number) => <span style={{ ...MONO, fontWeight: 700 }}>{v?.toLocaleString('en-IN')} kVA</span>,
    },
    {
      title: 'Mode of Supply', dataIndex: 'modeOfSupply',
      render: (v: string[]) => (
        <>{(v || []).map(m => <Tag key={m} color="geekblue" style={{ borderRadius: 6, marginBottom: 2 }}>{m}</Tag>)}</>
      ),
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => (
        <Tag style={{ borderRadius: 6, fontWeight: 600, color: '#fff', background: STATUS_COLOR[s] || '#8A93A6', border: 'none' }}>
          {s === 'active' ? 'Active' : s === 'suspended' ? 'Suspended' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Consumer) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => goView(record.id)} />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const col2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }
  const col3: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }

  // ── Full-page Detail view ─────────────────────────────────────
  if (mode === 'view') {
    if (!viewRecord) {
      if (masterDataLoaded) goList()
      return null
    }
    return (
      <MasterFormShell
        title={viewRecord.name}
        subtitle={`${viewRecord.code} · ${(viewRecord.services || []).length} HT service${(viewRecord.services || []).length !== 1 ? 's' : ''}`}
        onBack={goList}
        extra={
          <Space>
            {canEdit && (
              <Tooltip title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => goEdit(viewRecord.id)} />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this record?"
                  description="This action cannot be undone."
                  onConfirm={() => handleDelete(viewRecord.id)}
                  okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        }
      >
        <div>
          <div style={{ marginBottom: 16 }}>
            <Tag style={{ borderRadius: 6, fontWeight: 600, color: '#fff', background: STATUS_COLOR[viewRecord.status] || '#8A93A6', border: 'none', fontSize: 12 }}>
              {viewRecord.status === 'active' ? 'Active' : viewRecord.status === 'suspended' ? 'Suspended' : 'Inactive'}
            </Tag>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 8 }}>
            Company Details
          </div>
          <DetailRow label="Contact Person" value={viewRecord.contactPersonName} />
          <DetailRow label="Designation"    value={viewRecord.designation} />
          <DetailRow label="Mobile"         value={viewRecord.contactMobile} />
          <DetailRow label="Email"          value={viewRecord.contactEmail} />
          <DetailRow label="Company Address" value={viewRecord.companyAddress} />
          <DetailRow label="GST Number"     value={viewRecord.gstin ? <span style={MONO}>{viewRecord.gstin}</span> : undefined} />
          <DetailRow label="PAN Number"     value={viewRecord.panNumber ? <span style={MONO}>{viewRecord.panNumber}</span> : undefined} />
          <DetailRow label="CIN Number"     value={viewRecord.cinNumber ? <span style={MONO}>{viewRecord.cinNumber}</span> : undefined} />

          <Divider style={{ margin: '16px 0 8px' }} />
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 12 }}>
            HT Services ({(viewRecord.services || []).length})
          </div>
          {(viewRecord.services || []).map((srv, i) => (
            <div key={srv.id} style={{ background: '#f8f9fc', borderRadius: 8, padding: 12, marginBottom: 10, border: '1px solid #e8eaf0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: '#001A4D', fontSize: 13 }}>{srv.plantName || `Service ${i + 1}`} — {srv.htscNumber}</span>
                <Tag style={{ borderRadius: 6, fontSize: 11 }} color={srv.status === 'active' ? 'green' : 'default'}>{srv.status}</Tag>
              </div>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>{srv.plantAddress}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                {(srv.typeOfPower || []).map(t => <Tag key={t} style={{ borderRadius: 4, fontSize: 11 }}>{t}</Tag>)}
                {(srv.modeOfSupply || []).map(m => <Tag key={m} color="geekblue" style={{ borderRadius: 4, fontSize: 11 }}>{m}</Tag>)}
              </div>
              <DetailRow label="Sanctioned Demand" value={<span style={MONO}>{srv.sanctionedDemand?.toLocaleString('en-IN')} kVA</span>} />
              <DetailRow label="Drawl Voltage"     value={srv.drawlVoltage ? <span style={MONO}>{srv.drawlVoltage} kV</span> : undefined} />
              <DetailRow label="EDC"               value={srv.edc} />
              <DetailRow label="Tariff Category"   value={srv.powerTariffCategory} />
              <DetailRow label="Avg Annual Consumption" value={srv.avgAnnualConsumption ? <span style={MONO}>{srv.avgAnnualConsumption?.toLocaleString('en-IN')} kWh</span> : undefined} />
              <DetailRow label="Avg Monthly Consumption" value={srv.avgAnnualConsumption ? <span style={MONO}>{formatUnitsKWh(annualToMonthlyUnits(srv.avgAnnualConsumption))}</span> : undefined} />
              <DetailRow label="Avg Monthly Bill"  value={srv.avgMonthlyBill ? <span style={MONO}>₹{srv.avgMonthlyBill?.toLocaleString('en-IN')}</span> : undefined} />
            </div>
          ))}
        </div>
      </MasterFormShell>
    )
  }

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Consumer' : 'Add Consumer'}
        subtitle="HT consumer — one consumer can have multiple HT services"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>

          {/* ══ SECTION 1: Communication Details ══ */}
          <Card
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                <UserOutlined style={{ color: '#2563EB' }} /> Section 1 — Communication Details
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <div style={col2}>
              <Form.Item
                name="name"
                label="Consumer Company Name"
                tooltip="Pick a company from Consumer-type leads to auto-fill contact details"
                rules={[{ required: true, message: 'Required' }]}
              >
                <AutoComplete
                  options={leadCompanies.map(c => ({ value: c }))}
                  placeholder="Select from Consumer leads"
                  allowClear
                  onSelect={handleLeadCompanySelect}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item name="contactPersonName" label="Contact Person Name">
                <Input placeholder="e.g. Ramesh Kumar" />
              </Form.Item>
            </div>
            <div style={col3}>
              <Form.Item name="designation" label="Designation">
                <Input placeholder="e.g. General Manager" />
              </Form.Item>
              <Form.Item name="contactMobile" label="Mobile Number">
                <Input placeholder="e.g. 9876543210" maxLength={10} />
              </Form.Item>
              <Form.Item name="contactEmail" label="Email ID">
                <Input placeholder="e.g. name@company.com" type="email" />
              </Form.Item>
            </div>
            <Form.Item name="companyAddress" label="Company Address">
              <Input.TextArea rows={2} placeholder="Full registered address" />
            </Form.Item>
            <div style={col3}>
              <Form.Item name="gstin" label="GST Number">
                <Input placeholder="e.g. 33AAACT1234A1Z5" style={MONO} />
              </Form.Item>
              <Form.Item name="panNumber" label="PAN Number">
                <Input placeholder="e.g. AAACT1234A" style={{ ...MONO, textTransform: 'uppercase' }} maxLength={10} />
              </Form.Item>
              <Form.Item name="cinNumber" label="CIN Number">
                <Input placeholder="e.g. U17111TN2005PTC056789" style={MONO} />
              </Form.Item>
            </div>
            <div style={col3}>
              <Form.Item name="state" label="State">
                <Select showSearch placeholder="Select state" options={STATE_OPTIONS.map(s => ({ value: s, label: s }))} />
              </Form.Item>
              <Form.Item name="tdsRate" label="TDS Rate (%)">
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} placeholder="e.g. 2" />
              </Form.Item>
              <Form.Item name="paymentTerms" label="Payment Terms (days)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 30" />
              </Form.Item>
            </div>
            <Form.Item name="bankDetails" label="Bank Details">
              <Input placeholder="Bank — Account No — IFSC" />
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="active">
              <Select options={[
                { value: 'active',    label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
                { value: 'inactive',  label: 'Inactive' },
              ]} />
            </Form.Item>
          </Card>

          {/* ══ SECTION 2: HT Services ══ */}
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                  <ThunderboltOutlined style={{ color: '#2563EB' }} /> Section 2 — HT Services ({services.length})
                </span>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={openAddService}>
                  Add Service
                </Button>
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {services.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8A93A6', padding: '24px 0', fontSize: 13 }}>
                No HT services added yet. Click "Add Service" to add one.
              </div>
            ) : services.map((srv, idx) => (
              <div key={srv.id} style={{ background: '#f8f9fc', borderRadius: 8, padding: 12, marginBottom: 10, border: '1px solid #e8eaf0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#001A4D', fontSize: 13, marginBottom: 2 }}>
                      {srv.plantName || `Service ${idx + 1}`} — {srv.htscNumber}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>{srv.plantAddress}</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(srv.typeOfPower || []).map(t => <Tag key={t} style={{ borderRadius: 4, fontSize: 11 }}>{t}</Tag>)}
                      {(srv.modeOfSupply || []).map(m => <Tag key={m} color="geekblue" style={{ borderRadius: 4, fontSize: 11 }}>{m}</Tag>)}
                    </div>
                    <div style={{ fontSize: 11, color: '#8A93A6', marginTop: 4 }}>
                      {srv.sanctionedDemand?.toLocaleString('en-IN')} kVA · {srv.edc}
                      {srv.avgAnnualConsumption ? <span> · {Number(srv.avgAnnualConsumption).toLocaleString('en-IN')} kWh/yr (~{formatUnitsKWh(annualToMonthlyUnits(srv.avgAnnualConsumption))}/mo)</span> : null}
                    </div>
                  </div>
                  <Space>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditService(idx)} />
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteService(idx)} />
                  </Space>
                </div>
              </div>
            ))}

            {/* ── Inline Add / Edit HT Service editor (opens below, not a popup) ── */}
            {serviceModalOpen && (
              <div
                onKeyDown={(e) => {
                  // Enter in a plain input must not submit the outer Consumer form
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                    e.preventDefault()
                  }
                }}
                style={{
                  border: '1px dashed #94A3B8', borderRadius: 10,
                  padding: 16, marginTop: 12, background: '#FAFBFF',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: '#001A4D', marginBottom: 12 }}>
                  {editingServiceIdx !== null ? '✏️ Edit HT Service' : '➕ Add New HT Service'}
                </div>
                <Form form={serviceForm} layout="vertical" component={false}>
                  <Form.Item name="plantName" label="Plant Name" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="e.g. SIDCO Chennai Plant" />
                  </Form.Item>
                  <div style={col2}>
                    <Form.Item name="htscNumber" label="Consumer HTSC Number" rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="e.g. HT-TN-123456" style={MONO} />
                    </Form.Item>
                    <Form.Item name="powerTariffCategory" label="Power Tariff Category (TNEB)">
                      <Select showSearch placeholder="Select TNEB tariff" options={tnebTariffOptions} />
                    </Form.Item>
                  </div>

                  <Form.Item name="plantAddress" label="Plant Address">
                    <Input.TextArea rows={2} placeholder="Plant / factory address" />
                  </Form.Item>

                  <div style={col2}>
                    <Form.Item name="typeOfPower" label="Type of Power">
                      <Select mode="tags" placeholder="Select or type" options={typeOfPowerOptions} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="modeOfSupply" label="Mode of Supply">
                      <Select mode="tags" placeholder="Select or type" options={modeOfSupplyOptions} style={{ width: '100%' }} />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="sanctionedDemand" label="Sanctioned Demand (kVA)" rules={[{ required: true }]}>
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 1500" />
                    </Form.Item>
                    <Form.Item name="drawlVoltage" label="Drawl Voltage (kV)">
                      <Select showSearch placeholder="Select voltage" options={voltageOptions} allowClear />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="substationName" label="Substation Name">
                      <Input placeholder="e.g. SIDCO SS" />
                    </Form.Item>
                    <Form.Item name="substationVoltage" label="Substation Voltage (kV)">
                      <Select showSearch placeholder="Select voltage" options={voltageOptions} allowClear />
                    </Form.Item>
                  </div>

                  <Form.Item name="edc" label="EDC (Electricity Distribution Circle)">
                    <Select showSearch placeholder="Select EDC" options={edcOptions} allowClear />
                  </Form.Item>

                  <Form.Item name="existingPowerSupplier" label="Existing Power Supplier">
                    <Input placeholder="e.g. TANGEDCO" style={{ width: '100%' }} />
                  </Form.Item>

                  <div style={col2}>
                    <Form.Item
                      name="avgAnnualConsumption"
                      label="Avg Annual Consumption (kWh)"
                      extra={`≈ ${formatUnitsKWh(annualToMonthlyUnits(serviceConsumptionWatch))}/month`}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 5400000" />
                    </Form.Item>
                    <Form.Item name="avgMonthlyBill" label="Avg Monthly Bill (₹)">
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 3825000" />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="plantLatitude" label="Plant Latitude">
                      <Input placeholder="e.g. 13.0827" style={MONO} />
                    </Form.Item>
                    <Form.Item name="plantLongitude" label="Plant Longitude">
                      <Input placeholder="e.g. 80.2707" style={MONO} />
                    </Form.Item>
                  </div>

                  <Form.Item name="status" label="Status" initialValue="active">
                    <Select options={[
                      { value: 'active',   label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]} />
                  </Form.Item>
                </Form>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                  <Button onClick={() => { setServiceModalOpen(false); serviceForm.resetFields(); setEditingServiceIdx(null) }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSaveService}>
                    {editingServiceIdx !== null ? 'Update Service' : 'Add Service'}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {mode === 'edit' ? 'Update Consumer' : 'Add Consumer'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  if (!masterDataLoaded) return <TableSkeleton columns={5} />

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: '#2563EB' }} /> Consumer Master
          </h2>
          <p className="page-subtitle">Master records for HT consumers — one consumer can have multiple HT services</p>
        </div>
        {canAdd && <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Consumer</Button>}
      </div>

      {/* ── Stat cards ── */}
      <KpiCardRow
        cards={[
          { label: 'Total Consumers', value: consumers.length, icon: <TeamOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Avg Annual Consumption', value: formatIndianCompact(totalConsumption, { suffix: 'kWh/yr' }), icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
          { label: 'Avg Monthly Consumption', value: formatIndianCompact(annualToMonthlyUnits(totalConsumption), { suffix: 'kWh/mo' }), icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#0EA5E9' }} />, iconBg: 'rgba(14,165,233,0.08)', borderColor: '#0EA5E9' },
          { label: 'States Covered', value: statesCount, icon: <EnvironmentOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name, code or GST..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Suspended', value: 'suspended' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredConsumers}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} consumers` }}
          scroll={{ x: true }}
          onRow={(record) => ({ onClick: () => goView(record.id), style: { cursor: 'pointer' } })}
        />
      </Card>

    </div>
  )
}

export default CustomerMaster
